import { showRequestError } from '../../utils/request-feedback';
import { PermissionControl } from '../../permissions/access';
import { Input, Button, message, Select, Form, Row, Col, Drawer, Space, Spin, Typography, Radio } from 'antd';
import { fetchBountyTags } from '../../api/community';
import { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { conversionUtcDate } from '../../utils/comm';
import { createHotBounty, updateHotBounty } from '../../api/discover';

const HotBountyEditView = ({ open, defaultTag, onClose, onUpdate }) => {
    const [loading, setLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [form] = Form.useForm();
    const [topics, setTopics] = useState([]);
    const [data, setData] = useState({});

    useEffect(() => {
        setData(defaultTag);

        form.setFieldValue('search', null);
        if (defaultTag) {
            form.setFieldValue('tagId', defaultTag.tag.id)
            form.setFieldValue('name', `#${defaultTag.tag.name}`)
            form.setFieldValue('weight', defaultTag.weight);
            form.setFieldValue('status', String(defaultTag.status).toLowerCase() === 'active' ? 0 : 1);
        } else {
            resetFields();
        }
        getTopicTags('');
    }, [defaultTag]);

    const resetFields = () => {
        form.setFieldValue('search', null);
        form.setFieldValue('tagId', '')
        form.setFieldValue('name', '')
        form.setFieldValue('weight', '');
        form.setFieldValue('status', 0);
    }

    const onSubmit = () => {
        let params = {
            ...data,
            tagId: form.getFieldValue('tagId'),
            name: String(form.getFieldValue('name')).replace('#', ''),
            weight: form.getFieldValue('weight'),
            status: form.getFieldValue('status') ? parseInt(form.getFieldValue('status')) : 0,
        };
        delete params['post'];
        if (!params.tagId || !params.weight) {
            message.warning('Incomplete information');
            return;
        }
        setLoading(true);
        if (params.id) {
            updateHotBounty(params).then(res => {
                resetFields();

                onClose();
                onUpdate();
            }).catch(err => {
                showRequestError(err, err?.message);
            }).finally(() => {
                setLoading(false);
            })
        } else {
            createHotBounty(params).then(res => {
                resetFields();

                onClose();
                onUpdate();
            }).catch(err => {
                showRequestError(err, err?.message);
            }).finally(() => {
                setLoading(false);
            })
        }
    }

    const onSelectChange = (value) => {
        form.setFieldValue('tagId', value);
        let tag = topics.find(item => item.value === value);
        if (tag) {
            form.setFieldValue('name', `#${tag.label}`);
        }
    }

    const handleSearch = (value) => {
        getTopicTags(value);
    }

    const getTopicTags = useDebouncedCallback(
        (keyword) => {
            let params = {
                'page.num': 1,
                'page.size': 10,
                'tag': keyword,
            }
            setSearchLoading(true);
            fetchBountyTags(params).then(res => {
                if (res.list) {
                    let tmp = res.list.map(item => ({
                        createdAt: conversionUtcDate(item.createdAt, null, true),
                        label: item.name,
                        value: item.id,
                    }));
                    setTopics(tmp);
                }
            }).finally(() => setSearchLoading(false)).catch(showRequestError)
        },
        300
    )

    return (
        <div>
            <Drawer
                forceRender={true}
                title={defaultTag ? 'Edit hot bounty' : 'Add hot bounty'}
                width={480}
                maskClosable
                onClose={onClose}
                open={open}
                styles={{
                    body: {
                        paddingBottom: 80,
                    },
                }}
                extra={
                    <Space>
                        <Button onClick={onClose}>Cancel</Button>
                        <PermissionControl permission="hotbounty.control.button.2e793b9e" variant={(data?.id) ? 'update' : 'create'}><Button onClick={onSubmit} type="primary" loading={loading}>
                            Submit
                        </Button></PermissionControl>
                    </Space>
                }
            >
                <Form
                    layout="vertical"
                    form={form}
                    requiredMark={false} >

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name='search' label='Search Bounty'>
                                <Select
                                    style={{ width: '100%' }}
                                    size='large'
                                    placeholder='you can input keywords to search bounty'
                                    defaultActiveFirstOption={false}
                                    suffixIcon={null}
                                    filterOption={false}
                                    onSearch={handleSearch}
                                    notFoundContent={searchLoading ? <Spin /> : null}
                                    options={topics}
                                    optionRender={(option) => {
                                        return (
                                            <Space>
                                                <div style={{ flex: 1 }}>
                                                    <b style={{ wordBreak: 'break-word' }}>{option.data.label}</b>
                                                    <div>
                                                        <Typography.Text style={{ color: '#999', fontSize: 12 }}>
                                                            {option.data.createdAt}
                                                        </Typography.Text>
                                                    </div>
                                                </div>
                                            </Space>
                                        )
                                    }}
                                    onChange={onSelectChange}
                                    allowClear
                                    showSearch
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <div style={{ height: 36, width: 200 }} />

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name='tagId' label='Bounty ID'>
                                <Input placeholder="please input bounty id" allowClear />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name='name' label='Bounty Name'>
                                <Input.TextArea autoSize disabled />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name='weight' label='Weight'>
                                <Input placeholder="recommend weight" type='number' allowClear />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name='status' label='Status'>
                                <Radio.Group buttonStyle='solid' disabled={!data?.id || parseInt(data?.id) === 0}>
                                    <Radio.Button value={0}>Active</Radio.Button>
                                    <Radio.Button value={1}>Inactive</Radio.Button>
                                </Radio.Group>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Drawer>
        </div>
    )
}

export default HotBountyEditView;